import { memo, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Modal, Form, Button, Typography, Select, Radio, RadioChangeEvent } from 'antd';
import { HiringManager } from '../../../../fsm/jobs';

import styles from './index.module.css';

const { Text } = Typography;
const { Option } = Select;

interface Props {
  data: HiringManager[];
  isOpen: boolean;
  isLoading: boolean;
  hiringManagerId?: number;
  recruiterId?: number;
  onClose: () => void;
  onFetchHiringManagers: () => void;
  onFetchRecruiters: () => void;
  onAssignHiringManagers: (id: number, hiringManagerId: number) => void;
  onAssignRecruiter: (id: number, recruiterId: number) => void;
}

const HiringManagerModal = ({
  isOpen,
  isLoading,
  data,
  hiringManagerId,
  recruiterId,
  onClose,
  onFetchHiringManagers,
  onFetchRecruiters,
  onAssignHiringManagers,
  onAssignRecruiter,
}: Props) => {
  const [form] = Form.useForm();
  const { jobId } = useParams();
  const [roleType, setRoleType] = useState<'HIRING_MANAGER' | 'RECRUITER'>(
    'HIRING_MANAGER',
  );
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoadingUsers(true);
      // Determine initial role type based on existing assignments
      if (hiringManagerId) {
        setRoleType('HIRING_MANAGER');
        onFetchHiringManagers();
        form.setFieldValue('userId', hiringManagerId);
      } else if (recruiterId) {
        setRoleType('RECRUITER');
        onFetchRecruiters();
        form.setFieldValue('userId', recruiterId);
      } else {
        setRoleType('HIRING_MANAGER');
        onFetchHiringManagers();
      }
    }
  }, [isOpen, hiringManagerId, recruiterId]);

  useEffect(() => {
    // Enable select when data arrives
    if (data && data.length >= 0) {
      setIsLoadingUsers(false);
    }
  }, [data]);

  const handleRoleTypeChange = useCallback(
    (e: RadioChangeEvent) => {
      const newRoleType = e.target.value as 'HIRING_MANAGER' | 'RECRUITER';
      setRoleType(newRoleType);
      form.resetFields(['userId']);
      setIsLoadingUsers(true);
      
      if (newRoleType === 'HIRING_MANAGER') {
        onFetchHiringManagers();
      } else {
        onFetchRecruiters();
      }
    },
    [form, onFetchHiringManagers, onFetchRecruiters],
  );

  const handleSave = useCallback(async () => {
    try {
      await form.validateFields();
      const userId = form.getFieldValue('userId');
      
      if (jobId) {
        if (roleType === 'HIRING_MANAGER') {
          onAssignHiringManagers(parseInt(jobId), userId);
        } else {
          onAssignRecruiter(parseInt(jobId), userId);
        }
      }
      onClose();
    } catch (errorInfo) {
      console.error('Validation failed:', errorInfo);
    }
  }, [form, jobId, roleType, onClose, onAssignHiringManagers, onAssignRecruiter]);

  return (
    <Modal
      title="Assign Team Member"
      open={isOpen}
      confirmLoading={isLoading}
      onCancel={() => onClose()}
      footer={
        <>
          <Button onClick={() => onClose()}>Cancel</Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <Form
        form={form}
        layout="vertical"
        style={{ width: '100%' }}
        autoComplete="off"
      >
        <div className={styles.content}>
          <Form.Item label="Role Type">
            <Radio.Group value={roleType} onChange={handleRoleTypeChange}>
              <Radio value="HIRING_MANAGER">Hiring Manager</Radio>
              <Radio value="RECRUITER">Recruiter</Radio>
            </Radio.Group>
          </Form.Item>
          
          <Form.Item 
            name="userId" 
            label={roleType === 'HIRING_MANAGER' ? 'Select Hiring Manager' : 'Select Recruiter'}
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select 
              size="large" 
              placeholder={isLoadingUsers ? 'Loading users...' : 'Select a user'}
              disabled={isLoadingUsers}
              loading={isLoadingUsers}
            >
              {data?.map((item) => (
                <Option
                  key={item.id}
                  value={item.id}
                >{`${item.firstName} ${item.lastName}`}</Option>
              ))}
            </Select>
          </Form.Item>
          
          <Text type="secondary">
            {roleType === 'HIRING_MANAGER'
              ? 'Hiring managers can review and provide feedback when you share the list of candidates with them.'
              : 'Recruiters will be responsible for managing this job posting and its candidates.'}
          </Text>
        </div>
      </Form>
    </Modal>
  );
};

export default memo(HiringManagerModal);
